// @ts-strict-ignore
import range from 'lodash/range';

import {SectionItem} from 'common/components/items-menu/types';
import {getAlphaLabelFromIndex} from 'common/util';
import {RowLabelType} from 'common/query-builder/query-entry-section/types';
import {StepSelection} from 'common/report/funnels/models/types';
import {Translatable} from 'common/i18n';

import {RangeSelection} from './types';

export function generateStepRanges({
  entry,
  steps,
  rowLabelType,
  disableAllSteps,
  disableEnteringFunnel,
  isReportConsistency,
  onlyAllSteps,
}: {
  entry: StepSelection;
  steps: number;
  rowLabelType?: RowLabelType;
  disableAllSteps?: boolean;
  disableEnteringFunnel?: boolean;
  isReportConsistency?: boolean;
  onlyAllSteps?: boolean;
}): Array<SectionItem & {value: RangeSelection}> {
  let hasMatch = false;
  const ranges = steps ? range(isReportConsistency ? 0 : 1, steps) : [];
  const {from: eventFrom, to: eventTo} = entry.range;

  const allEventsItem: SectionItem & {value: RangeSelection} = {
    isSelected: entry.isEntireRange,
    label: ({t}) => t(`query-builder.step-selection.all-steps`),
    tooltip: isReportConsistency
      ? {
          text: ({t}) => t(`query-builder.step-selection.all-steps-tooltip`),
        }
      : null,
    value: {isEntireRange: true, range: {}},
  };

  if (disableAllSteps) {
    allEventsItem.isDisabled = true;
    allEventsItem.tooltip = {text: ({t}) => t(`query-builder.step-selection.all-steps-disabled-tooltip`)};
  }

  const rangeItems = ranges.map((i) => {
    const _range: RangeSelection[`range`] = {from: i, to: i + 1};
    const isSelected = eventFrom === _range.from && eventTo === _range.to;
    hasMatch = hasMatch || isSelected;

    const from = rowLabelType === RowLabelType.Alpha ? getAlphaLabelFromIndex(_range.from - 1) : _range.from;
    const to = rowLabelType === RowLabelType.Alpha ? getAlphaLabelFromIndex(_range.to - 1) : _range.to;
    const label =
      isReportConsistency && from === 0
        ? ({t}) => t(`query-builder.step-selection.entering-funnel`)
        : ({t}) => t(`query-builder.step-selection.step-from-to`, {from, to});

    let tooltip: Translatable =
      from === 0
        ? ({t}) => t(`query-builder.step-selection.step-entering-tooltip`)
        : ({t}) => t(`query-builder.step-selection.step-range-tooltip`, {to});

    let isDisabled = onlyAllSteps;
    if (onlyAllSteps) {
      tooltip = ({t}) => t(`query-builder.step-selection.only-all-steps-tooltip`);
    }
    if (disableEnteringFunnel && from === 0) {
      isDisabled = true;
      tooltip = ({t}) => t(`query-builder.step-selection.entering-disabled-tooltip`);
    }

    return {
      isSelected,
      isDisabled,
      label,
      tooltip: isReportConsistency ? {text: tooltip, 'max-width': `200px`} : null,
      value: {isEntireRange: false, range: _range},
    };
  });

  // if event has no match, it means a multi step
  // range was previously selected
  if (!entry.isEntireRange && !hasMatch) {
    rangeItems.push({
      isSelected: true,
      isDisabled: false,
      label: ({t}) => t(`query-builder.step-selection.step-from-to-long`, {from: eventFrom, to: eventTo}),
      tooltip: null,
      value: {isEntireRange: false, range: entry.range},
    });
  }

  return [allEventsItem, ...rangeItems];
}
